import { SwapRightOutlined } from '@ant-design/icons';
import { Flex, InputNumber, Space, Typography } from 'antd';
import { InputNumberProps } from 'antd/lib/input-number';
import classNames from 'classnames';
import { z } from 'zod';

import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';
import { notEmpty } from 'technical/utils/not-empty';

import { ClearAllNumberRangeInput } from './clear-all-number-range-input';
import styles from './index.module.scss';

const numberOrEmptySchema = z.union([z.number(), z.null(), z.undefined()]);

export const numberRangeSchema = z.union([
  z.tuple([numberOrEmptySchema, numberOrEmptySchema]),
  z.null(),
]);
export type NumberRangeValue = z.infer<typeof numberRangeSchema>;

export type NumberRangeInputProps = {
  value?: NumberRangeValue;
  onChange?: (value: NumberRangeValue) => void;
  allowClear?: boolean;
  defaultValues?: NumberRangeValue;
  leftInputProps?: InputNumberProps;
  leftTitle?: string;
  rightInputProps?: InputNumberProps;
  rightTitle?: string;
  errorMessage?: string;
  className?: string;
  bottomMessage?: string;
};

function NumberRangeInput({
  value,
  onChange,
  leftInputProps,
  leftTitle,
  rightInputProps,
  rightTitle,
  errorMessage,
  className,
  allowClear,
  defaultValues,
  bottomMessage,
}: Readonly<NumberRangeInputProps>) {
  const leftInputId = `leftInput-${leftTitle}`;
  const rightInputId = `rightInput-${rightTitle}`;

  const isValueDefined =
    value?.length == 2 &&
    (!isUndefinedOrNull(value.at(0)) || !isUndefinedOrNull(value.at(1)));

  const clearAll = () => {
    if (!value || !onChange) {
      return;
    }
    onChange([defaultValues?.at(0), defaultValues?.at(1)]);
  };

  return (
    <Flex
      vertical
      className={classNames(styles.rangeInputContainer, className)}
    >
      <Space
        className={classNames(
          styles.rangeInput,
          //customPicker is duplicate of 'ant-input ant-picker ant-picker-range ant-picker-outlined',
          styles.customPicker,
        )}
      >
        <Space>
          {leftTitle ? (
            <label htmlFor={leftInputId}>
              <Typography.Text type="secondary">{leftTitle}</Typography.Text>
            </label>
          ) : null}
          <InputNumber
            {...leftInputProps}
            variant={'borderless'}
            size={'small'}
            id={leftTitle ? leftInputId : undefined}
            value={notEmpty(value?.[0]) ? value[0] : defaultValues?.at(0)}
            onChange={(inputValue) => {
              if (!onChange) {
                return;
              }

              if (typeof inputValue !== 'string') {
                onChange([
                  inputValue ?? defaultValues?.at(1),
                  value ? value[1] : undefined,
                ]);
                return;
              }
              onChange([defaultValues?.at(0), value ? value[1] : undefined]);
            }}
          />
        </Space>
        <SwapRightOutlined
          className={
            // duplicate of native ant class 'ant-picker-separator'
            styles.customPickerSeparator
          }
        />
        <Space>
          {rightTitle ? (
            <label htmlFor={rightInputId}>
              <Typography.Text type="secondary">{rightTitle}</Typography.Text>
            </label>
          ) : null}
          <InputNumber
            {...rightInputProps}
            id={rightTitle ? rightInputId : undefined}
            value={notEmpty(value?.[1]) ? value[1] : defaultValues?.at(1)}
            variant={'borderless'}
            size={'small'}
            onChange={(inputValue) => {
              if (!onChange) {
                return;
              }

              if (typeof inputValue !== 'string') {
                onChange([
                  value ? value[0] : undefined,
                  inputValue ?? defaultValues?.at(1),
                ]);
                return;
              }
              onChange([value ? value[0] : undefined, defaultValues?.at(1)]);
            }}
          />
        </Space>
        <ClearAllNumberRangeInput
          allowClear={allowClear}
          isValueDefined={isValueDefined}
          onClick={clearAll}
        />
      </Space>
      {errorMessage ? (
        <Typography.Text type="danger">{errorMessage}</Typography.Text>
      ) : null}
      {bottomMessage ? (
        <Typography.Text
          italic
          type="secondary"
          className={styles.bottomMessage}
        >
          {bottomMessage}
        </Typography.Text>
      ) : null}
    </Flex>
  );
}

export default NumberRangeInput;
